import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { Download, Info } from 'lucide-react';

export const DownloadTestComponent: React.FC = () => {
  const downloadStore = useDownloadFlowStore();
  const [testLog, setTestLog] = useState<string[]>([]);

  const addLog = (message: string) => {
    setTestLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testPublicDownload = () => {
    addLog('Starting public URL download test...');
    
    // Simulate a public URL download
    const link = document.createElement('a');
    link.href = 'https://example.com/test-file.zip';
    link.download = 'test-sequence.zip';
    link.style.display = 'none';
    
    // Log the download attempt
    addLog(`Created download link with filename: ${link.download}`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    addLog('Download link clicked - check browser downloads');
  };

  const testModalFlow = () => {
    addLog('Starting modal flow test...');
    
    // Start a download session
    downloadStore.startDownloadSession({
      sequenceId: 'test-123',
      sequenceTitle: 'Test Sequence',
      sequenceArtist: 'Test Artist',
      isOwner: false,
      isFree: true,
      price: 0,
      userId: 'user-123',
    });
    
    addLog('Download session started');
    
    // Trigger modal display
    if (downloadStore.shouldShowAddedToDisplayModal()) {
      downloadStore.showAddedToDisplayModal();
      addLog('AddedToDisplayModal triggered');
    } else {
      addLog('AddedToDisplayModal should not show (suppressed or already shown)');
    }
  };

  const clearTestLog = () => {
    setTestLog([]);
    downloadStore.reset();
    addLog('Test log and download store cleared');
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Download System Test
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={testPublicDownload} variant="outline">
            Test Download (.zip)
          </Button>
          <Button onClick={testModalFlow} variant="outline">
            Test Modal Flow
          </Button>
          <Button onClick={clearTestLog} variant="destructive">
            Clear Log
          </Button>
        </div>
        
        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Info className="h-4 w-4" />
            Current Store State
          </h3>
          <div className="text-sm space-y-1">
            <p>Session Active: {downloadStore.currentSession ? 'Yes' : 'No'}</p>
            <p>AddedToDisplay Modal: {downloadStore.modalState.isAddedToDisplayModalVisible ? 'Visible' : 'Hidden'}</p>
            <p>Confirmation Page: {downloadStore.modalState.isConfirmationPageVisible ? 'Visible' : 'Hidden'}</p>
            <p>Suppress Modal: {downloadStore.userPreferences.suppressAddedToDisplayModal ? 'Yes' : 'No'}</p>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold mb-2">Test Log</h3>
          <div className="text-sm space-y-1 font-mono">
            {testLog.length === 0 ? (
              <p className="text-gray-500">No test activity yet...</p>
            ) : (
              testLog.map((log, index) => (
                <p key={index} className="text-gray-700">{log}</p>
              ))
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DownloadTestComponent;